import {
    useCallback, useMemo, useState
} from 'react';

import fromCache from 'Util/Cache/Cache';
import {
    getMaxQuantity, getMinQuantity, getName, getPrice, getProductInStock
} from 'Util/Product/Extract';

// You can extend props for defaultQuantity, etc. as needed
/** @namespace Bodypwa/Hooks/UseProductCardState/useProductCardState */
export function useProductCardState({
    product,
    defaultEnteredOptions = [],
    defaultSelectedOptions = [],
    defaultQuantity = 1,
    adjustedPrice
}) {
    // Options logic
    const [enteredOptions, setEnteredOptions] = useState(defaultEnteredOptions);
    const [selectedOptions, setSelectedOptions] = useState(defaultSelectedOptions);
    // Quantity logic (grouped products might be objects)
    const [quantity, setQuantity] = useState(defaultQuantity);
    const {
        inStock, maxQuantity, minQuantity, productName, productPrice
    } = useMemo(() => {
        if (!product || typeof product !== 'object') {
            return {
                inStock: false,
                maxQuantity: 0,
                minQuantity: 0,
                productName: '',
                productPrice: null
            };
        }
        const priceRange = product.price_range || {};
        const dynamicPrice = !!product.dynamic_price;
        const type = product.type_id;
        const options = product.options || [];
        return {
            inStock: fromCache(getProductInStock, [product]),
            maxQuantity: getMaxQuantity(product),
            minQuantity: getMinQuantity(product),
            productName: getName(product),
            productPrice: fromCache(getPrice, [priceRange, dynamicPrice, adjustedPrice, type, options])
        };
    }, [product, adjustedPrice]);

    // Optionally: setters with validation or side effects
    const updateEnteredOptions = useCallback((opts) => setEnteredOptions(opts), []);
    const updateSelectedOptions = useCallback((opts) => setSelectedOptions(opts), []);
    const updateQuantity = useCallback((qty) => setQuantity(qty), []);

    return {
        inStock,
        maxQuantity,
        minQuantity,
        productName,
        productPrice,
        quantity,
        setQuantity: updateQuantity,
        enteredOptions,
        setEnteredOptions: updateEnteredOptions,
        selectedOptions,
        setSelectedOptions: updateSelectedOptions
    };
}
